import React, { useState, useEffect } from "react";
import {
  Container,
  Text,
  Card,
  Group,
  Badge,
  Button,
  Stack,
  Grid,
  Avatar,
  ActionIcon,
  Modal,
  Image,
  Divider,
  Paper,
  Tooltip,
  Progress,
  Timeline,
  ThemeIcon,
  Select,
} from "@mantine/core";
import {
  IconMapPin,
  IconClock,
  IconUsers,
  IconBriefcase,
  IconHeart,
  IconShare,
  IconArrowLeft,
  IconChevronRight,
  IconBuilding,
  IconCalendar,
  IconTrendingUp,
  IconAward,
  IconCode,
  IconPalette,
  IconSpeakerphone,
  IconDatabase,
  IconShield,
  IconBulb,
  IconRocket,
  IconStar,
  IconCheck,
  IconChevronDown,
} from "@tabler/icons-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "./styles.scss";

interface Job {
  id: string;
  title: string;
  description: string;
  department: string;
  location: string;
  type: "full-time" | "part-time" | "contract" | "internship";
  level: "entry" | "mid" | "senior" | "executive";
  salary: {
    min: number;
    max: number;
    currency: string;
  };
  postedDate: string;
  deadline: string;
  featured: boolean;
  requirements: string[];
  benefits: string[];
  skills: string[];
  remote: boolean;
}

interface Benefit {
  icon: React.ReactNode;
  title: string;
  description: string;
}

interface TeamMember {
  name: string;
  role: string;
  avatar: string;
  quote: string;
}

const Careers: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [filter, setFilter] = useState<string>("all");
  const [likedJobs, setLikedJobs] = useState<Set<string>>(new Set());

  // Enforce light mode for careers page
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", "light");
    localStorage.setItem("app-theme", "light");
  }, []);

  // const jobs: Job[] = [
  //   {
  //     id: "1",
  //     title: "Senior Full-Stack Developer",
  //     description:
  //       "Join our engineering team to build scalable web applications using cutting-edge technologies. You'll work on both frontend and backend systems, collaborating with designers and product managers.",
  //     department: "Engineering",
  //     location: "San Francisco, CA",
  //     type: "full-time",
  //     level: "senior",
  //     salary: { min: 140000, max: 180000, currency: "USD" },
  //     postedDate: "2024-01-15",
  //     deadline: "2024-02-28",
  //     featured: true,
  //     requirements: [
  //       "5+ years of full-stack development experience",
  //       "Proficiency in React, Node.js, and TypeScript",
  //       "Experience with cloud platforms (AWS, GCP, or Azure)",
  //       "Strong understanding of database design and optimization",
  //       "Experience with CI/CD and DevOps practices",
  //     ],
  //     benefits: [
  //       "Competitive salary and equity package",
  //       "Comprehensive health, dental, and vision insurance",
  //       "Flexible PTO and work-from-home options",
  //       "Annual learning and development budget",
  //       "Top-tier equipment and workspace",
  //     ],
  //     skills: ["React", "Node.js", "TypeScript", "AWS", "PostgreSQL"],
  //     remote: true,
  //   },
  //   {
  //     id: "2",
  //     title: "UX/UI Designer",
  //     description:
  //       "Create exceptional user experiences for our educational platform. You'll design intuitive interfaces, conduct user research, and collaborate closely with our product and engineering teams.",
  //     department: "Design",
  //     location: "New York, NY",
  //     type: "full-time",
  //     level: "mid",
  //     salary: { min: 90000, max: 120000, currency: "USD" },
  //     postedDate: "2024-01-20",
  //     deadline: "2024-03-15",
  //     featured: true,
  //     requirements: [
  //       "3+ years of UX/UI design experience",
  //       "Proficiency in Figma, Sketch, or Adobe Creative Suite",
  //       "Strong portfolio demonstrating design thinking",
  //       "Experience with user research and testing",
  //       "Understanding of frontend development principles",
  //     ],
  //     benefits: [
  //       "Creative freedom and autonomy",
  //       "Design tools and software budget",
  //       "Conference and workshop attendance",
  //       "Collaborative and inspiring work environment",
  //       "Flexible working hours",
  //     ],
  //     skills: [
  //       "Figma",
  //       "Sketch",
  //       "Prototyping",
  //       "User Research",
  //       "Design Systems",
  //     ],
  //     remote: true,
  //   },
  //   {
  //     id: "3",
  //     title: "Product Marketing Manager",
  //     description:
  //       "Drive product adoption and growth through strategic marketing initiatives. You'll develop go-to-market strategies, create compelling content, and analyze market trends.",
  //     department: "Marketing",
  //     location: "Austin, TX",
  //     type: "full-time",
  //     level: "mid",
  //     salary: { min: 85000, max: 115000, currency: "USD" },
  //     postedDate: "2024-01-25",
  //     deadline: "2024-03-01",
  //     featured: false,
  //     requirements: [
  //       "3+ years of product marketing experience",
  //       "Strong analytical and data-driven mindset",
  //       "Excellent written and verbal communication",
  //       "Experience with marketing automation tools",
  //       "B2B SaaS experience preferred",
  //     ],
  //     benefits: [
  //       "Performance-based bonuses",
  //       "Professional development opportunities",
  //       "Marketing conference attendance",
  //       "Collaborative team environment",
  //       "Stock option program",
  //     ],
  //     skills: [
  //       "Product Marketing",
  //       "Analytics",
  //       "Content Strategy",
  //       "HubSpot",
  //       "SEO",
  //     ],
  //     remote: false,
  //   },
  //   {
  //     id: "4",
  //     title: "DevOps Engineer",
  //     description:
  //       "Build and maintain our cloud infrastructure, implement CI/CD pipelines, and ensure system reliability and scalability. You'll work with cutting-edge cloud technologies.",
  //     department: "Engineering",
  //     location: "Seattle, WA",
  //     type: "full-time",
  //     level: "senior",
  //     salary: { min: 130000, max: 170000, currency: "USD" },
  //     postedDate: "2024-02-01",
  //     deadline: "2024-03-20",
  //     featured: false,
  //     requirements: [
  //       "4+ years of DevOps/SRE experience",
  //       "Expertise in AWS, Docker, and Kubernetes",
  //       "Experience with Infrastructure as Code (Terraform, CloudFormation)",
  //       "Strong scripting skills (Python, Bash)",
  //       "Experience with monitoring and alerting systems",
  //     ],
  //     benefits: [
  //       "Cutting-edge technology stack",
  //       "On-call compensation",
  //       "Technical conference budget",
  //       "Home office setup allowance",
  //       "Professional certification support",
  //     ],
  //     skills: ["AWS", "Kubernetes", "Docker", "Terraform", "Python"],
  //     remote: true,
  //   },
  //   {
  //     id: "5",
  //     title: "Data Scientist",
  //     description:
  //       "Extract insights from educational data to improve learning outcomes. You'll build predictive models, create data visualizations, and work with large datasets.",
  //     department: "Data",
  //     location: "Boston, MA",
  //     type: "full-time",
  //     level: "mid",
  //     salary: { min: 110000, max: 140000, currency: "USD" },
  //     postedDate: "2024-02-05",
  //     deadline: "2024-03-10",
  //     featured: true,
  //     requirements: [
  //       "3+ years of data science experience",
  //       "Proficiency in Python, R, and SQL",
  //       "Experience with machine learning frameworks",
  //       "Strong statistical analysis skills",
  //       "Experience with data visualization tools",
  //     ],
  //     benefits: [
  //       "Access to premium datasets",
  //       "Research publication opportunities",
  //       "ML/AI conference attendance",
  //       "Flexible research time",
  //       "Collaboration with academic institutions",
  //     ],
  //     skills: ["Python", "R", "SQL", "TensorFlow", "Tableau"],
  //     remote: true,
  //   },
  //   {
  //     id: "6",
  //     title: "Frontend Developer Intern",
  //     description:
  //       "Join our development team as an intern to gain hands-on experience building modern web applications. Perfect opportunity for students or recent graduates.",
  //     department: "Engineering",
  //     location: "Remote",
  //     type: "internship",
  //     level: "entry",
  //     salary: { min: 25, max: 35, currency: "USD" },
  //     postedDate: "2024-02-10",
  //     deadline: "2024-04-01",
  //     featured: false,
  //     requirements: [
  //       "Currently pursuing Computer Science or related degree",
  //       "Basic knowledge of HTML, CSS, and JavaScript",
  //       "Familiarity with React or similar frameworks",
  //       "Eagerness to learn and grow",
  //       "Strong problem-solving skills",
  //     ],
  //     benefits: [
  //       "Mentorship from senior developers",
  //       "Real-world project experience",
  //       "Flexible schedule around studies",
  //       "Potential for full-time offer",
  //       "Tech industry networking",
  //     ],
  //     skills: ["JavaScript", "React", "HTML", "CSS", "Git"],
  //     remote: true,
  //   },
  // ];

  const jobs: Job[] = [];
  const benefits: Benefit[] = [
    {
      icon: <IconHeart size={24} />,
      title: "Comprehensive Health Coverage",
      description: "Full medical, dental, and vision insurance for you and your family",
    },
    {
      icon: <IconTrendingUp size={24} />,
      title: "Career Growth",
      description: "Continuous learning opportunities and clear advancement paths",
    },
    {
      icon: <IconBulb size={24} />,
      title: "Innovation Time",
      description: "20% time for personal projects and creative exploration",
    },
    {
      icon: <IconRocket size={24} />,
      title: "Startup Equity",
      description: "Competitive equity package with high growth potential",
    },
    {
      icon: <IconCalendar size={24} />,
      title: "Flexible Schedule",
      description: "Work when you're most productive with flexible hours",
    },
    {
      icon: <IconBuilding size={24} />,
      title: "Beautiful Workspace",
      description: "Modern offices with all the amenities you need",
    },
  ];

  const teamMembers: TeamMember[] = [
    {
      name: "Sarah Chen",
      role: "Head of Engineering",
      avatar:
        "https://images.unsplash.com/photo-1494790108755-2616b612b786?w=150&h=150&fit=crop&crop=face",
      quote:
        "We're building the future of education with cutting-edge technology and passionate people.",
    },
    {
      name: "Marcus Johnson",
      role: "Lead Designer",
      avatar:
        "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      quote:
        "Every design decision we make is focused on creating meaningful learning experiences.",
    },
    {
      name: "Elena Rodriguez",
      role: "VP of Product",
      avatar:
        "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=150&h=150&fit=crop&crop=face",
      quote: "We're not just building features; we're empowering learners to achieve their dreams.",
    },
  ];

  const filteredJobs = jobs.filter((job) => {
    if (filter === "all") return true;
    if (filter === "remote") return job.remote;
    return job.department.toLowerCase() === filter.toLowerCase();
  });

  const toggleLike = (jobId: string) => {
    const newLikedJobs = new Set(likedJobs);
    if (newLikedJobs.has(jobId)) {
      newLikedJobs.delete(jobId);
    } else {
      newLikedJobs.add(jobId);
    }
    setLikedJobs(newLikedJobs);
  };

  const getDepartmentIcon = (department: string) => {
    switch (department.toLowerCase()) {
      case "engineering":
        return <IconCode size={16} />;
      case "design":
        return <IconPalette size={16} />;
      case "marketing":
        return <IconSpeakerphone size={16} />;
      case "data":
        return <IconDatabase size={16} />;
      default:
        return <IconBriefcase size={16} />;
    }
  };

  const getDepartmentColor = (department: string) => {
    switch (department.toLowerCase()) {
      case "engineering":
        return "blue";
      case "design":
        return "pink";
      case "marketing":
        return "orange";
      case "data":
        return "green";
      default:
        return "gray";
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case "entry":
        return "green";
      case "mid":
        return "yellow";
      case "senior":
        return "orange";
      case "executive":
        return "red";
      default:
        return "gray";
    }
  };

  const languages = [
    { value: "en", label: "English", flag: "🇬🇧" },
    { value: "fr", label: "Français", flag: "🇫🇷" },
    { value: "pt", label: "Português", flag: "🇵🇹" },
    { value: "de", label: "Deutsch", flag: "🇩🇪" },
  ];

  const changeLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
  };

  return (
    <div className="careers-page">
      {/* Hero Section */}
      <div className="careers-hero">
        <div className="careers-hero-bg" />
        <div className="careers-hero-particles">
          {[...Array(15)].map((_, i) => (
            <div key={i} className={`particle particle-${i + 1}`} />
          ))}
        </div>
        <Container size="xl" className="careers-hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Group justify="space-between" align="center" mb="xl">
              <Button
                variant="subtle"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => navigate("/")}
                className="back-button"
              >
                {t("careers.backToHome")}
              </Button>

              {/* Language Switcher */}
              <Select
                value={i18n.language}
                onChange={(value) => value && changeLanguage(value)}
                data={languages.map((lang) => ({
                  value: lang.value,
                  label: `${lang.flag} ${lang.label}`,
                }))}
                className="language-selector"
                size="xs"
                w={180}
                rightSection={<IconChevronDown size={12} />}
                styles={{
                  input: {
                    padding: "0 8px",
                    fontSize: "0.8rem",
                    height: "26px",
                    borderRadius: "8px",
                  },
                  dropdown: {
                    padding: "4px",
                  },
                  option: {
                    fontSize: "0.8rem",
                    padding: "4px 8px",
                  },
                }}
              />
            </Group>

            <Text className="careers-hero-title" mb="md">
              {t("careers.title")}
            </Text>
            <Text className="careers-hero-subtitle" mb="xl">
              {t("careers.subtitle")}
            </Text>
            <Group gap="md" className="hero-stats">
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  50+
                </Text>
                <Text size="sm" c="dimmed">
                  Team Members
                </Text>
              </div>
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  0
                </Text>
                <Text size="sm" c="dimmed">
                  Open Positions
                </Text>
              </div>
              <div className="stat-item">
                <Text size="xl" fw={700} c="violet">
                  4.9
                </Text>
                <Text size="sm" c="dimmed">
                  Glassdoor Rating
                </Text>
              </div>
            </Group>
          </motion.div>
        </Container>
      </div>

      {/* Benefits Section */}
      <Container size="xl" mt="xl" mb="xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Text className="section-title" ta="center" mb="xl">
            {t("careers.whyWorkHere")}
          </Text>
          <Grid gutter="xl">
            {benefits.map((benefit, index) => (
              <Grid.Col key={benefit.title} span={{ base: 12, md: 6, lg: 4 }}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Card className="benefit-card" radius="xl" p="xl">
                    <ThemeIcon
                      size={60}
                      radius="xl"
                      variant="gradient"
                      gradient={{ from: "violet", to: "blue" }}
                      mb="md"
                      className="benefit-icon"
                    >
                      {benefit.icon}
                    </ThemeIcon>
                    <Text size="lg" fw={700} mb="xs" className="benefit-title">
                      {benefit.title}
                    </Text>
                    <Text size="sm" c="dimmed" className="benefit-description">
                      {benefit.description}
                    </Text>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </motion.div>
      </Container>

      {/* Team Section */}
      {/* HIDDEN  */}
      {/* <Container size="xl" mb="xl">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Text className="section-title" ta="center" mb="xl">
            Meet Our Team
          </Text>
          <Grid gutter="xl">
            {teamMembers.map((member, index) => (
              <Grid.Col key={member.name} span={{ base: 12, md: 4 }}>
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -8 }}
                >
                  <Card className="team-card" radius="xl" p="xl">
                    <Stack align="center" gap="md">
                      <Avatar src={member.avatar} size={80} radius="xl" className="team-avatar" />
                      <div style={{ textAlign: "center" }}>
                        <Text size="lg" fw={700} className="team-name">
                          {member.name}
                        </Text>
                        <Text size="sm" c="dimmed" className="team-role">
                          {member.role}
                        </Text>
                      </div>
                      <Text
                        size="sm"
                        ta="center"
                        className="team-quote"
                        style={{ fontStyle: "italic" }}
                      >
                        "{member.quote}"
                      </Text>
                    </Stack>
                  </Card>
                </motion.div>
              </Grid.Col>
            ))}
          </Grid>
        </motion.div>
      </Container> */}

      {/* Jobs Section */}
      <Container size="xl" mb="xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <Text className="section-title" ta="center" mb="md">
            Open Positions
          </Text>
          <Text ta="center" c="dimmed" mb="xl" size="lg">
            Find your perfect role and join our mission to transform education
          </Text>

          {/* Filter Buttons */}
          <Group justify="center" mb="xl">
            <Button.Group>
              {["all", "engineering", "design", "marketing", "data", "remote"].map((filterType) => (
                <Button
                  key={filterType}
                  variant={filter === filterType ? "filled" : "outline"}
                  color="violet"
                  onClick={() => setFilter(filterType)}
                  leftSection={filterType === "remote" ? <IconBuilding size={16} /> : undefined}
                  className="filter-button"
                >
                  {filterType.charAt(0).toUpperCase() + filterType.slice(1)}
                </Button>
              ))}
            </Button.Group>
          </Group>

          {/* Jobs Grid */}
          <Grid gutter="xl">
            <AnimatePresence>
              {filteredJobs.map((job, index) => (
                <Grid.Col key={job.id} span={{ base: 12, md: 6 }}>
                  <motion.div
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.4, delay: index * 0.05 }}
                    whileHover={{ y: -8 }}
                    className="job-card-container"
                  >
                    <Card
                      className={`job-card ${job.featured ? "featured" : ""}`}
                      radius="xl"
                      p="lg"
                    >
                      {job.featured && (
                        <Badge
                          className="featured-badge"
                          variant="gradient"
                          gradient={{ from: "orange", to: "red" }}
                        >
                          Featured
                        </Badge>
                      )}

                      <Stack gap="md">
                        <Group justify="space-between" align="flex-start">
                          <div style={{ flex: 1 }}>
                            <Group gap="xs" mb="xs">
                              <Badge
                                color={getDepartmentColor(job.department)}
                                variant="light"
                                leftSection={getDepartmentIcon(job.department)}
                                size="sm"
                              >
                                {job.department}
                              </Badge>
                              {job.remote && (
                                <Badge variant="light" color="green" size="sm">
                                  Remote
                                </Badge>
                              )}
                            </Group>
                            <Text size="xl" fw={700} className="job-title" mb="xs">
                              {job.title}
                            </Text>
                            <Text size="sm" c="dimmed" lineClamp={2} className="job-description">
                              {job.description}
                            </Text>
                          </div>
                          <ActionIcon
                            variant="subtle"
                            color={likedJobs.has(job.id) ? "red" : "gray"}
                            radius="xl"
                            onClick={() => toggleLike(job.id)}
                            className="like-button"
                          >
                            <IconHeart
                              size={18}
                              fill={likedJobs.has(job.id) ? "currentColor" : "none"}
                            />
                          </ActionIcon>
                        </Group>

                        <Group gap="md" wrap="wrap">
                          <Group gap="xs">
                            <IconMapPin size={16} color="var(--mantine-color-violet-6)" />
                            <Text size="sm" fw={500}>
                              {job.location}
                            </Text>
                          </Group>
                          <Group gap="xs">
                            <IconClock size={16} color="var(--mantine-color-violet-6)" />
                            <Text size="sm" fw={500}>
                              {job.type}
                            </Text>
                          </Group>
                          <Badge color={getLevelColor(job.level)} variant="light" size="sm">
                            {job.level}
                          </Badge>
                        </Group>

                        <Group gap="xs" wrap="wrap">
                          {job.skills.slice(0, 4).map((skill) => (
                            <Badge key={skill} variant="outline" color="violet" size="xs">
                              {skill}
                            </Badge>
                          ))}
                          {job.skills.length > 4 && (
                            <Badge variant="outline" color="gray" size="xs">
                              +{job.skills.length - 4}
                            </Badge>
                          )}
                        </Group>

                        <Divider />

                        <Group justify="space-between" align="center">
                          <div>
                            <Text size="lg" fw={700} c="violet">
                              {job.type === "internship"
                                ? `$${job.salary.min}-${job.salary.max}/hr`
                                : `$${(job.salary.min / 1000).toFixed(0)}K - $${(
                                    job.salary.max / 1000
                                  ).toFixed(0)}K`}
                            </Text>
                            <Text size="xs" c="dimmed">
                              Posted {dayjs(job.postedDate).fromNow()}
                            </Text>
                          </div>
                          <Button
                            variant="gradient"
                            gradient={{ from: "violet", to: "blue" }}
                            radius="xl"
                            rightSection={<IconChevronRight size={16} />}
                            onClick={() => setSelectedJob(job)}
                            className="apply-button"
                          >
                            View Details
                          </Button>
                        </Group>
                      </Stack>
                    </Card>
                  </motion.div>
                </Grid.Col>
              ))}
            </AnimatePresence>
          </Grid>
        </motion.div>
      </Container>

      {/* Job Detail Modal */}
      <Modal
        opened={!!selectedJob}
        onClose={() => setSelectedJob(null)}
        size="lg"
        radius="xl"
        centered
        overlayProps={{ backgroundOpacity: 0.55, blur: 3 }}
        className="job-modal"
      >
        {selectedJob && (
          <div className="job-modal-content">
            <Stack gap="lg">
              <Group justify="space-between" align="start">
                <div style={{ flex: 1 }}>
                  <Group gap="xs" mb="xs">
                    <Badge
                      color={getDepartmentColor(selectedJob.department)}
                      variant="light"
                      leftSection={getDepartmentIcon(selectedJob.department)}
                    >
                      {selectedJob.department}
                    </Badge>
                    {selectedJob.remote && (
                      <Badge variant="light" color="green">
                        Remote
                      </Badge>
                    )}
                    <Badge color={getLevelColor(selectedJob.level)} variant="light">
                      {selectedJob.level}
                    </Badge>
                  </Group>
                  <Text size="xl" fw={700} mb="xs">
                    {selectedJob.title}
                  </Text>
                  <Text c="dimmed" mb="md">
                    {selectedJob.description}
                  </Text>
                </div>
              </Group>

              <Group gap="xl">
                <Group gap="xs">
                  <IconMapPin size={20} color="var(--mantine-color-violet-6)" />
                  <div>
                    <Text fw={600}>{selectedJob.location}</Text>
                    <Text size="sm" c="dimmed">
                      Location
                    </Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <IconClock size={20} color="var(--mantine-color-violet-6)" />
                  <div>
                    <Text fw={600}>{selectedJob.type}</Text>
                    <Text size="sm" c="dimmed">
                      Type
                    </Text>
                  </div>
                </Group>
                <Group gap="xs">
                  <IconBriefcase size={20} color="var(--mantine-color-violet-6)" />
                  <div>
                    <Text fw={600}>
                      {selectedJob.type === "internship"
                        ? `$${selectedJob.salary.min}-${selectedJob.salary.max}/hr`
                        : `$${(selectedJob.salary.min / 1000).toFixed(0)}K - $${(
                            selectedJob.salary.max / 1000
                          ).toFixed(0)}K`}
                    </Text>
                    <Text size="sm" c="dimmed">
                      Salary
                    </Text>
                  </div>
                </Group>
              </Group>

              <Divider />

              <div>
                <Text size="lg" fw={700} mb="md">
                  Requirements
                </Text>
                <Stack gap="xs">
                  {selectedJob.requirements.map((req, index) => (
                    <Group key={index} gap="xs" align="flex-start">
                      <IconCheck
                        size={16}
                        color="var(--mantine-color-green-6)"
                        style={{ marginTop: 2 }}
                      />
                      <Text size="sm">{req}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>

              <div>
                <Text size="lg" fw={700} mb="md">
                  Benefits
                </Text>
                <Stack gap="xs">
                  {selectedJob.benefits.map((benefit, index) => (
                    <Group key={index} gap="xs" align="flex-start">
                      <IconStar
                        size={16}
                        color="var(--mantine-color-violet-6)"
                        style={{ marginTop: 2 }}
                      />
                      <Text size="sm">{benefit}</Text>
                    </Group>
                  ))}
                </Stack>
              </div>

              <div>
                <Text size="lg" fw={700} mb="md">
                  Required Skills
                </Text>
                <Group gap="xs">
                  {selectedJob.skills.map((skill) => (
                    <Badge key={skill} variant="light" color="violet">
                      {skill}
                    </Badge>
                  ))}
                </Group>
              </div>

              <Button
                variant="gradient"
                gradient={{ from: "violet", to: "blue" }}
                size="lg"
                radius="xl"
                rightSection={<IconRocket size={20} />}
                fullWidth
                className="modal-apply-button"
              >
                Apply for this Position
              </Button>
            </Stack>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Careers;
